"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { TOAST_ANIMATION_DURATION_MS, TOAST_BACKGROUNDS, TOAST_ICONS } from "./constants";
import type { IToastProps } from "./types";

export function Toast({ message, type, onClose, autoDismissMs }: IToastProps) {
  const Icon = TOAST_ICONS[type];
  const [isClosing, setIsClosing] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const beginClose = () => {
    setIsClosing(true);
    setTimeout(() => onCloseRef.current(), TOAST_ANIMATION_DURATION_MS);
  };

  useEffect(() => {
    if (!autoDismissMs) return;
    const timer = setTimeout(beginClose, autoDismissMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDismissMs]);

  return (
    <div
      data-testid="toast"
      data-ui-toast=""
      role="status"
      aria-live="polite"
      className={cn(
        // Sheet/Dialog set body { pointer-events: none } while open, to block interaction
        // outside their own layer. The toast lives outside that layer (its own body-level
        // portal), so it inherits `none` and clicks fall through to whatever is beneath it
        // unless pointer-events is re-enabled explicitly here.
        "pointer-events-auto fixed inset-x-4 bottom-6 z-[70] flex items-center justify-between gap-5 rounded-lg px-6 py-5 shadow-lg text-sm sm:text-base text-white duration-300 sm:inset-x-auto sm:right-6",
        TOAST_BACKGROUNDS[type],
        isClosing
          ? "animate-out fade-out slide-out-to-bottom-4 sm:slide-out-to-bottom-0 sm:zoom-out-95"
          : "animate-in fade-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 sm:slide-in-from-right-4"
      )}
    >
      <div className="flex items-center gap-3">
        <Icon aria-hidden className="shrink-0 text-xl" />
        <span>{message}</span>
      </div>
      <button
        onClick={beginClose}
        className="text-white/80 hover:text-white transition-colors text-xl leading-none"
      >
        x
      </button>
    </div>
  );
}
